import * as React from 'react';
import { cn } from '@/lib/utils';
import { FlowerIcon, TriangleCorner } from '@/design-system/club-ui/shapes';

export interface DecoratedFrameProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** タイトルテキスト */
  title: string;
  /** タイトルセクションの背景色（デフォルト: #FC86A1） */
  backgroundColor?: string;
  /** コンテナの幅（デフォルト: 350） */
  width?: number | string;
  /** デコレーションを表示するか（デフォルト: true） */
  showDecorations?: boolean;
  /** TriangleCornerの色（デフォルト: #FF497A） */
  cornerColor?: string;
  /** FlowerIconの色（デフォルト: white） */
  flowerIconColor?: string;
  /** コンテンツ */
  children: React.ReactNode;
}

/**
 * DecoratedFrame - 装飾付きフレームコンポーネント
 *
 * ピンク色の背景タイトルと、三角装飾を持つ汎用的なフレームです。
 * 主にお悩みリストなどのコンテンツを表示するために使用します。
 *
 * @example
 * ```tsx
 * // デフォルト
 * <DecoratedFrame title="IUMさんのお悩み">
 *   <div>コンテンツ</div>
 * </DecoratedFrame>
 *
 * // デコレーションなし
 * <DecoratedFrame
 *   title="お悩み"
 *   showDecorations={false}
 * >
 *   <div>コンテンツ</div>
 * </DecoratedFrame>
 *
 * // カスタム色
 * <DecoratedFrame
 *   title="IUMさんのお悩み"
 *   backgroundColor="#FF9BA0"
 *   cornerColor="#FF497A"
 *   flowerIconColor="white"
 * >
 *   <div>コンテンツ</div>
 * </DecoratedFrame>
 * ```
 */
export const DecoratedFrame = React.forwardRef<
  HTMLDivElement,
  DecoratedFrameProps
>(
  (
    {
      className,
      title,
      backgroundColor = '#FC86A1',
      width = 350,
      showDecorations = true,
      cornerColor = '#FF497A',
      flowerIconColor = 'white',
      children,
      style,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col relative', className)}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          ...style,
        }}
        {...props}
      >
        {/* タイトルセクション */}
        <div
          className="flex justify-center w-full py-club-12"
          style={{ backgroundColor, color: 'white' }}
        >
          {/* 花アイコンデコレーション */}
          {showDecorations && (
            <>
              {/* 左カラム: flex-end */}
              <div className="flex items-end">
                <FlowerIcon width={14} height={15} color={flowerIconColor} />
              </div>
            </>
          )}

          {/* タイトルテキスト */}
          <p className="text-[14px] font-medium leading-[1.6] tracking-[0.08em] [font-feature-settings:'palt'] mx-club-8">
            {title}
          </p>

          {/* 花アイコンデコレーション */}
          {showDecorations && (
            <>
              {/* 右カラム: flex-start */}
              <div className="flex items-start">
                <FlowerIcon width={14} height={15} color={flowerIconColor} />
              </div>
            </>
          )}
        </div>

        {/* triangle + コンテンツ + triangle をflexで横並び */}
        <div className="flex w-full items-start">
          {/* 左側のtriangle - 上下反転、ネガティブマージンで上にずらす */}
          {showDecorations && (
            <div style={{ transform: 'rotate(180deg)', marginTop: '-6.4px' }}>
              <TriangleCorner
                size={20}
                color={cornerColor}
                position="bottom-left"
              />
            </div>
          )}

          {/* コンテンツセクション */}
          <div className="flex-1 min-w-0">{children}</div>

          {/* 右側のtriangle - 上下反転、左右反転、ネガティブマージンで上にずらす */}
          {showDecorations && (
            <div
              style={{
                transform: 'rotate(180deg) scaleX(-1)',
                marginTop: '-6.4px',
              }}
            >
              <TriangleCorner
                size={20}
                color={cornerColor}
                position="bottom-left"
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

DecoratedFrame.displayName = 'DecoratedFrame';
